import { useEffect, useState } from "react";
import { previewAmazonPublish, publishAmazon } from "../../api/client";
import type { AmzChange, AmzPreviewResponse, AmzProductDetail, AmzPublishResponse, AmzSource, AmzValidation } from "../../types/amazon";
import { Badge, Spinner } from "./AmzUi";
import { errorMessage } from "./helpers";
import type { Tone } from "./helpers";

type Phase = "loading" | "diff" | "confirm" | "sending" | "done" | "error";

interface PublishModalProps {
  detail: AmzProductDetail;
  /** 保留中の下書き保存を完了させる。失敗時 false。 */
  flush: () => Promise<boolean>;
  onClose: () => void;
  onPublished: (res: AmzPublishResponse, changes: AmzChange[]) => void;
  onGoHistory: () => void;
}

const KIND_LABEL: Record<AmzChange["kind"], string> = {
  text: "Text Changed",
  attribute: "Attribute Changed",
  image: "Image Changed",
};

const VAL_LABEL: Record<AmzValidation["status"], { label: string; tone: Tone }> = {
  ok: { label: "OK", tone: "success" },
  warn: { label: "警告", tone: "warning" },
  error: { label: "エラー", tone: "error" },
  skip: { label: "未実行", tone: "neutral" },
};

function sourceLabel(s: AmzSource | null): string {
  return s === "AI" ? "AI提案" : "手動";
}

function devText(detail: AmzProductDetail, res: AmzPublishResponse): string {
  const d = res.developer;
  return [
    `Request ID   ${d.requestId ?? "—"}`,
    `API          ${d.api}`,
    `Operation    ${d.operation}`,
    `Endpoint     ${d.endpoint}`,
    `Marketplace  ${d.marketplace}`,
    `Status Code  ${d.statusCode}`,
    `Result       ${res.result.status}`,
    `Submission   ${res.result.submissionId ?? "—"}`,
    "",
    "Request JSON",
    JSON.stringify(d.requestJson, null, 2),
    "",
    "writeConfirmed = true",
    `SKU          ${detail.sku}`,
  ].join("\n");
}

export function PublishModal({ detail, flush, onClose, onPublished, onGoHistory }: PublishModalProps) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [preview, setPreview] = useState<AmzPreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [result, setResult] = useState<AmzPublishResponse | null>(null);
  const [sentChanges, setSentChanges] = useState<AmzChange[]>([]);
  const [devOpen, setDevOpen] = useState(false);

  // 開いたら下書きを確定保存してから preview を取る。
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await flush();
      if (cancelled) return;
      if (!ok) {
        setError("下書きの保存に失敗しました。通信状況を確認して、もう一度お試しください。");
        setPhase("error");
        return;
      }
      try {
        const p = await previewAmazonPublish(detail.sku);
        if (cancelled) return;
        setPreview(p);
        setPhase("diff");
      } catch (e) {
        if (cancelled) return;
        setError(errorMessage(e));
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail.sku, flush]);

  const writeLive = preview?.writeLive ?? detail.writeLive;
  const blocked = !preview || !preview.canPublish;
  const stepIdx = phase === "diff" || phase === "loading" || phase === "error" ? 0 : phase === "confirm" ? 1 : 2;
  const title = phase === "diff" || phase === "loading" || phase === "error" ? "変更内容の確認" : "Amazonへ反映";
  const n = preview?.changes.length ?? 0;

  const send = async () => {
    if (!preview || !confirmed || !writeLive) return;
    setPhase("sending");
    setSentChanges(preview.changes);
    try {
      const res = await publishAmazon(detail.sku, { writeConfirmed: true, changesHash: preview.changesHash });
      setResult(res);
      setPhase("done");
      onPublished(res, preview.changes);
    } catch (e) {
      setError(errorMessage(e));
      setPhase("error");
    }
  };

  const isPut = preview?.operation === "PUT";

  return (
    <div className="amz-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="amz-modal">
        <div className="amz-modal-head">
          <h2>{title}</h2>
          <div className="amz-steps">
            {["1 差分", "2 確認", "3 送信"].map((label, i) => (
              <span key={label} className={`amz-step ${i === stepIdx ? "now" : i < stepIdx ? "done" : ""}`}>
                {label}
              </span>
            ))}
          </div>
        </div>

        {phase === "loading" && (
          <div className="amz-sending">
            <Spinner size="lg" />
            差分と送信前チェックを確認しています…
          </div>
        )}

        {phase === "error" && (
          <>
            <div className="amz-modal-body">
              <div className="amz-form-err">{error}</div>
              {error?.includes("下書きが変更されています") && <span className="amz-hint">一度閉じて、もう一度「変更を確認」を開いてください。</span>}
            </div>
            <div className="amz-modal-foot end">
              <button type="button" className="amz-btn" onClick={onClose}>
                閉じる
              </button>
            </div>
          </>
        )}

        {phase === "diff" && preview && (
          <>
            <div className="amz-modal-body">
              <div className="amz-diff-list">
                <span className="amz-sec-label">
                  Amazon現在値 → ローカル下書き（{n}件）· {isPut ? "新規登録（PUT）" : "更新（PATCH）"}
                </span>
                {preview.changes.map((c) => (
                  <div key={c.key} className="amz-diff-item">
                    <div className="amz-diff-item-head">
                      <Badge size="sm" mono>
                        {KIND_LABEL[c.kind]}
                      </Badge>
                      <b>{c.label}</b>
                      <span className="src">{sourceLabel(c.source)}</span>
                    </div>
                    <div className="amz-diff-old">{c.oldValue || "（未設定）"}</div>
                    <div className="amz-diff-new">{c.newValue || "（削除）"}</div>
                  </div>
                ))}
              </div>
              <div className="amz-vals">
                <span className="amz-sec-label">送信前チェック</span>
                {preview.validations.map((v) => {
                  const st = VAL_LABEL[v.status];
                  return (
                    <div key={v.name} className="amz-val-row">
                      <span className="name">{v.name}</span>
                      <Badge tone={st.tone}>{st.label}</Badge>
                      <span className="detail">
                        {v.detail}
                        {v.items && v.items.length > 0 && (
                          <ul>
                            {v.items.map((it, i) => (
                              <li key={i}>{it}</li>
                            ))}
                          </ul>
                        )}
                      </span>
                    </div>
                  );
                })}
                {preview.amazonIssues.length > 0 && (
                  <div className="amz-val-row">
                    <span className="name">Amazonの指摘</span>
                    <Badge tone="warning">{preview.amazonIssues.length}件</Badge>
                    <span className="detail">
                      <ul style={{ marginTop: 0 }}>
                        {preview.amazonIssues.map((i, k) => (
                          <li key={k}>
                            [{i.severity}] {i.message}
                          </li>
                        ))}
                      </ul>
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="amz-modal-foot">
              <span className="amz-block-msg">
                {preview.changes.length === 0 ? "反映する変更がありません" : blocked ? "エラーを解消するまで送信できません" : ""}
              </span>
              <div className="amz-modal-foot-btns">
                <button type="button" className="amz-btn" onClick={onClose}>
                  キャンセル
                </button>
                <button type="button" className="amz-btn primary" disabled={blocked} onClick={() => setPhase("confirm")}>
                  次へ
                </button>
              </div>
            </div>
          </>
        )}

        {phase === "confirm" && preview && (
          <>
            <div className="amz-confirm-body">
              <strong>{isPut ? "Amazonに商品を新規登録します。" : "Amazon商品情報を更新します。"}</strong>
              <div className="amz-kv3">
                <span>商品</span>
                <span>{detail.summary.itemName ?? detail.sku}</span>
                <span>SKU</span>
                <span className="amz-mono">{detail.sku}</span>
                <span>ASIN</span>
                <span className="amz-mono">{detail.asin ?? "—（未登録）"}</span>
                <span>変更</span>
                <div className="list">
                  {preview.changes.map((c) => (
                    <span key={c.key}>{c.label}</span>
                  ))}
                </div>
              </div>
              {!writeLive && (
                <div className="amz-warn-box">
                  本番書込ゲートが閉じています（AMAZON_LISTING_WRITE_LIVE）。このデプロイでは、Amazonへの反映はできません。
                </div>
              )}
              <label className="amz-check">
                <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
                上記の変更内容を確認しました
              </label>
              <span>Amazonへ送信しますか？</span>
            </div>
            <div className="amz-modal-foot end">
              <div className="amz-modal-foot-btns">
                <button type="button" className="amz-btn" onClick={() => setPhase("diff")}>
                  戻る
                </button>
                <button type="button" className="amz-btn primary" disabled={!confirmed || !writeLive} onClick={() => void send()}>
                  Amazonへ反映
                </button>
              </div>
            </div>
          </>
        )}

        {phase === "sending" && (
          <div className="amz-sending">
            <Spinner size="lg" />
            Amazonへ送信しています…
          </div>
        )}

        {phase === "done" && result && (
          <>
            <div className="amz-done-body">
              <div className="amz-done-head">
                <span className="amz-done-mark">✓</span>
                <strong>Amazonへ反映しました</strong>
              </div>
              <span className="amz-done-msg">
                {sentChanges.length}件の変更を送信しました（{result.result.status}）。Amazonの商品ページに表示されるまで数分かかる場合があります。
                {result.result.issues.length > 0 && ` Amazonから ${result.result.issues.length}件の指摘が返っています（Issuesタブで確認できます）。`}
              </span>
              <div className="amz-dev">
                <button type="button" className="amz-dev-toggle" onClick={() => setDevOpen((v) => !v)}>
                  Developer Details
                  <span>{devOpen ? "閉じる" : "開く"}</span>
                </button>
                {devOpen && <pre>{devText(detail, result)}</pre>}
              </div>
            </div>
            <div className="amz-modal-foot end">
              <div className="amz-modal-foot-btns">
                <button type="button" className="amz-btn" onClick={onGoHistory}>
                  履歴を見る
                </button>
                <button type="button" className="amz-btn dark" onClick={onClose}>
                  閉じる
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
